import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeveloperStore } from '../../store/developer-store';
import { buildContentPath, buildRulesIndexPath, isRulesLang, type RulesLang } from '../../seo/routes';
import './LegalFooter.css';

interface LegalFooterProps {
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToFaq?: () => void;
  onNavigateToRules?: () => void;
}

/** Tocchi consecutivi sul copyright che accendono (o spengono) la modalita' sviluppatore. */
const TAPS_TO_TOGGLE = 10;
/**
 * Distanza massima fra due tocchi perche' contino come consecutivi. Dieci tocchi
 * voluti arrivano a 150-400ms l'uno dall'altro: due secondi sono larghi per chi
 * lo fa apposta e stretti abbastanza perche' un tocco distratto non si sommi a
 * un tentativo cominciato minuti prima.
 */
const TAP_WINDOW_MS = 2000;

/** Legal footer shown on the home screen for both visitors and authenticated users. */
export default function LegalFooter({
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToFaq,
  onNavigateToRules,
}: LegalFooterProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';
  const isDeveloper = useDeveloperStore((s) => s.isDeveloper);
  const toggleDeveloper = useDeveloperStore((s) => s.toggleDeveloper);

  // Ref e non state: il conteggio in corso non deve ridisegnare il footer nove
  // volte, e non deve sopravvivere allo smontaggio — a sopravvivere e' solo il
  // flag che accende, che sta nello store.
  const tapCount = useRef(0);
  const lastTapAt = useRef(0);

  const handleCopyrightTap = () => {
    const now = Date.now();
    // Una pausa piu' lunga della finestra vuol dire "nuovo tentativo": si
    // riparte da uno e non da zero, perche' questo tocco e' gia' il primo di
    // quello nuovo.
    tapCount.current = now - lastTapAt.current > TAP_WINDOW_MS ? 1 : tapCount.current + 1;
    lastTapAt.current = now;
    if (tapCount.current >= TAPS_TO_TOGGLE) {
      tapCount.current = 0;
      toggleDeveloper();
    }
  };

  return (
    /*
     * Link VERI, non bottoni.
     *
     * Il click normale resta una navigazione dentro la SPA — `preventDefault` piu'
     * la callback, lo stesso patto di VariantsShowcase — ma l'`href` c'e', e con
     * lui il tasto centrale, «apri in una nuova scheda», e soprattutto un crawler
     * che segue i collegamenti. Le duecentoquaranta pagine regole erano isole
     * raggiungibili solo dal sitemap: questo footer e' il ponte, ed e' il motivo
     * per cui sta anche nell'HTML pre-renderizzato della landing.
     */
    <footer className="home-footer">
      {onNavigateToRules && (
        <>
          <a
            href={buildRulesIndexPath(lang)}
            onClick={(e) => { e.preventDefault(); onNavigateToRules(); }}
          >
            {t('rules.eyebrow')}
          </a>
          <span className="home-footer-sep" aria-hidden="true">·</span>
        </>
      )}
      <a
        href={buildContentPath(lang, 'terms')}
        onClick={(e) => { e.preventDefault(); onNavigateToTerms?.(); }}
      >
        {t('legal.termsTitle')}
      </a>
      <span className="home-footer-sep" aria-hidden="true">·</span>
      <a
        href={buildContentPath(lang, 'privacy')}
        onClick={(e) => { e.preventDefault(); onNavigateToPrivacy?.(); }}
      >
        {t('legal.privacyTitle')}
      </a>
      {onNavigateToFaq && (
        <>
          <span className="home-footer-sep" aria-hidden="true">·</span>
          <a
            href={buildContentPath(lang, 'faq')}
            onClick={(e) => { e.preventDefault(); onNavigateToFaq(); }}
          >
            {t('nav.faq')}
          </a>
        </>
      )}
      <span className="home-footer-sep" aria-hidden="true">·</span>
      {/* "DraughtsGame.com" e' il nome del sito — letterale, uguale in tutte le lingue.
          Vedi packages/web/src/__tests__/i18n-brand-terms.test.ts.
          E' un <button> vero e non uno <span> cliccabile: dieci tocchi accendono la
          modalita' sviluppatore, e chi naviga da tastiera deve poterci arrivare. Niente
          title/aria-label e niente aria-pressed: direbbero il segreto ad alta voce
          proprio a chi la battuta visiva non la vede. */}
      <button type="button" className="home-footer-copyright" onClick={handleCopyrightTap}>
        © 2026 DraughtsGame.com
      </button>
      {isDeveloper && (
        <>
          <span className="home-footer-sep" aria-hidden="true">·</span>
          <span className="home-footer-version">{t('common.version', { version: __APP_VERSION__ })}</span>
        </>
      )}
    </footer>
  );
}
